/**
 * ResourceEditorForm
 * Metadata form fields with CMS-style layout and content/media slot insertion points.
 * Gaps addressed: G10-1 (author), G10-2 (authorPro), G10-3 (toggles), FD-1 (regenerate), G8 (tags wired)
 */

import { type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { TypeSelector } from './TypeSelector';
// CategorySelector is a generic multi-select — reused here for external authors.
import { CategorySelector } from './CategorySelector';
import { TagSelector } from './TagSelector';
import { AuthorProSelector } from './AuthorProSelector';
import { ToggleButton } from './ToggleButton';

type FormValues = {
  title: string;
  seoUrl: string;
  seoTitle: string;
  seoDescription: string;
  typeId: string;
  categoryIds: string[];
  tagIds: string[];
  authorIds: string[];
  authorProIds: string[];
  isFeatured: boolean;
  isFeaturedForPro: boolean;
  isBquestRecommended: boolean;
  publishedAt: string;
  affiliateLink: string;
};

type OptionItem = { id: string; name: string; isActive?: boolean };
type InactiveTypeOption = { id: string; name: string };

type ResourceEditorFormProps = {
  values: FormValues;
  onChange: (field: string, value: unknown) => void;
  typeOptions: OptionItem[];
  categoryOptions: OptionItem[];
  tagOptions: OptionItem[];
  authorOptions?: OptionItem[];
  /** id -> display name for the currently-selected authorPros (from the loaded resource). */
  initialAuthorProNames?: Record<string, string>;
  errors?: Record<string, string>;
  onCreateTag?: (name: string) => Promise<{ id: string; name: string }>;
  inactiveSelectedTags?: OptionItem[];
  inactiveSelectedType?: InactiveTypeOption;
  contentSlot?: ReactNode;
  mediaSlot?: ReactNode;
};

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const INPUT =
  'mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-[#026857] focus:ring-1 focus:ring-[#026857]';
const LABEL = 'block text-sm font-medium text-gray-700';

export function ResourceEditorForm({
  values,
  onChange,
  typeOptions,
  categoryOptions,
  tagOptions,
  authorOptions = [],
  initialAuthorProNames,
  errors = {},
  onCreateTag,
  inactiveSelectedTags = [],
  inactiveSelectedType,
  contentSlot,
  mediaSlot,
}: ResourceEditorFormProps) {
  const handleTitleChange = (newTitle: string) => {
    onChange('title', newTitle);
    onChange('seoUrl', slugify(newTitle));
  };

  const handleRegenerateSeoUrl = () => {
    onChange('seoUrl', slugify(values.title));
  };

  const handleTagToggle = (tagId: string) => {
    const current = values.tagIds || [];
    const updated = current.includes(tagId)
      ? current.filter((id) => id !== tagId)
      : [...current, tagId];
    onChange('tagIds', updated);
  };

  return (
    <div
      data-testid="resource-editor-form"
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-5"
    >
      {/* Row: Title | SEO URL */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="resource-title" className={LABEL}>Title *</label>
          <input
            id="resource-title"
            type="text"
            value={values.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            className={INPUT}
            placeholder="Enter resource title"
          />
          {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
        </div>
        <div>
          <label htmlFor="resource-seo-url" className={LABEL}>SEO URL</label>
          <div className="relative">
            <input
              id="resource-seo-url"
              type="text"
              value={values.seoUrl}
              onChange={(e) => onChange('seoUrl', e.target.value)}
              className={`${INPUT} pr-10`}
              placeholder="auto-generated-from-title"
            />
            <button
              type="button"
              onClick={handleRegenerateSeoUrl}
              className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 p-1 rounded text-gray-400 hover:text-[#026857] hover:bg-gray-100 transition-colors group"
              aria-label="Regenerate SEO URL from title"
              title="Regenerate from title"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
          {errors.seoUrl && <p className="mt-1 text-sm text-red-600">{errors.seoUrl}</p>}
        </div>
      </div>

      {/* Row: SEO Title | SEO Description */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="resource-seo-title" className={LABEL}>SEO Title</label>
          <input
            id="resource-seo-title"
            type="text"
            value={values.seoTitle}
            onChange={(e) => onChange('seoTitle', e.target.value)}
            className={INPUT}
            placeholder="Optional SEO title"
          />
        </div>
        <div>
          <label htmlFor="resource-seo-desc" className={LABEL}>SEO Description</label>
          <input
            id="resource-seo-desc"
            type="text"
            value={values.seoDescription}
            onChange={(e) => onChange('seoDescription', e.target.value)}
            className={INPUT}
            placeholder="Optional SEO description"
          />
        </div>
      </div>

      {contentSlot && (
        <div className="border-t border-gray-100 pt-5">{contentSlot}</div>
      )}

      {/* Row: Type | Categories (G3-1, G3-2: searchable dropdowns + chips) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-100 pt-5">
        <TypeSelector
          value={values.typeId}
          options={typeOptions}
          onChange={(id) => onChange('typeId', id)}
          inactiveSelectedType={inactiveSelectedType}
          error={errors.typeId}
        />
        <CategorySelector
          options={categoryOptions}
          selectedIds={values.categoryIds}
          onChange={(ids) => onChange('categoryIds', ids)}
        />
      </div>

      {/* Tags */}
      <TagSelector
        tagOptions={tagOptions}
        selectedTagIds={values.tagIds}
        onToggle={handleTagToggle}
        onCreateTag={onCreateTag}
        inactiveSelectedTags={inactiveSelectedTags}
      />

      {mediaSlot && (
        <div className="border-t border-gray-100 pt-5">{mediaSlot}</div>
      )}

      {/* Row: Authors (external) | Authors Pro (SP/FA) — both multi-select */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-100 pt-5">
        <CategorySelector
          label="Authors"
          noun="authors"
          options={authorOptions}
          selectedIds={values.authorIds}
          onChange={(ids) => onChange('authorIds', ids)}
        />
        <AuthorProSelector
          value={values.authorProIds}
          onChange={(ids: string[]) => onChange('authorProIds', ids)}
          initialNames={initialAuthorProNames}
        />
      </div>

      {/* Row: Published At | Affiliate Link */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-100 pt-5">
        <div>
          <label htmlFor="resource-published" className={LABEL}>Published At</label>
          <input
            id="resource-published"
            type="date"
            value={values.publishedAt}
            onChange={(e) => onChange('publishedAt', e.target.value)}
            className={INPUT}
          />
        </div>
        <div>
          <label htmlFor="resource-affiliate" className={LABEL}>Affiliate Link</label>
          <input
            id="resource-affiliate"
            type="url"
            value={values.affiliateLink}
            onChange={(e) => onChange('affiliateLink', e.target.value)}
            className={INPUT}
            placeholder="https://"
          />
        </div>
      </div>

      {/* Feature toggles (G10-3: replaced checkboxes with toggle buttons) */}
      <div className="flex items-center gap-6">
        <ToggleButton
          label="Featured"
          checked={values.isFeatured}
          onChange={(v) => onChange('isFeatured', v)}
        />
        <ToggleButton
          label="Featured for Pro"
          checked={values.isFeaturedForPro}
          onChange={(v) => onChange('isFeaturedForPro', v)}
        />
        <ToggleButton
          label="bQuest Recommended"
          checked={values.isBquestRecommended}
          onChange={(v) => onChange('isBquestRecommended', v)}
        />
      </div>
    </div>
  );
}
